import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { LoadingView, DashStyles as Styles } from '../components/dashboard/DashboardUI';
import { useDashboardData } from '../components/dashboard/useDashboardData';
import { useNicheLabels } from '../components/dashboard/useNicheLabels';
import { About } from '../controllers/about/About';
import { Billing } from '../controllers/dashboard/Billing';
import { Bookings } from '../controllers/dashboard/Bookings';
import { BusinessProfile } from '../controllers/dashboard/BusinessProfile';
import { CallDetail } from '../controllers/dashboard/CallDetail';
import { Calls } from '../controllers/dashboard/Calls';
import { Help } from '../controllers/dashboard/Help';
import { Integrations } from '../controllers/dashboard/Integrations';
import { Invoices } from '../controllers/dashboard/Invoices';
import { Knowledge } from '../controllers/dashboard/Knowledge';
import { KnowledgeEdit } from '../controllers/dashboard/KnowledgeEdit';
import { Leads } from '../controllers/dashboard/Leads';
import { Login } from '../controllers/dashboard/Login';
import { Notifications } from '../controllers/dashboard/Notifications';
import { OnboardingWizard } from '../controllers/dashboard/OnboardingWizard';
import { Overview } from '../controllers/dashboard/Overview';
import { TextDetail } from '../controllers/dashboard/TextDetail';
import { Texts } from '../controllers/dashboard/Texts';
import { Landing } from '../controllers/landing/Landing';
import { Constants } from '../internal/Constants';
import { Localized } from '../internal/Localized';
import { DashboardProvider, useDashboard } from '../providers/DashboardProvider';
import { LobbySettingsProvider } from '../providers/LobbySettingsProvider';
import { LocaleProvider, useLocale } from '../providers/LocaleProvider';
import { ThemeProvider } from '../providers/ThemeProvider';
import { AuthAdapter, LayoutProvider, useAuth } from '@app/engine';
import { FormatLocalized } from '@shared/common';
import { TenantClient } from '@shared/frontend';
import { useMemo } from 'react';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

/** File-local constants for App. */
const localConstants = {
  dashboardWildcardRoute: '/dashboard/*',
  catchAllRoute: '*',
  // React Router basename must match Vite's `base` (without trailing slash) so absolute deep-links like
  // `/web/apps/lobby/calls` match the `/calls` route. In dev/preview Vite serves at `/` so basename is `/`.
  routerBasename: (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || '/'
} as const;

/**
 * A full-viewport loading state for the dashboard shell.
 *
 * @returns The full-screen loading element.
 */
function FullScreenLoading() {
  return (
    <div className={Styles.root}>
      <LoadingView />
    </div>
  );
}

/**
 * Wraps a screen in the dashboard shell with the given title.
 *
 * @param props - Component props.
 * @param props.titleKey - The localization key for the page title.
 * @param props.children - The screen content.
 * @returns The shell-wrapped screen.
 */
function Screen({ titleKey, children }: { titleKey: string; children: ReactNode }) {
  const { langVersion } = useLocale();
  const title = useMemo(() => {
    void langVersion;
    return Localized[titleKey] ?? titleKey;
  }, [langVersion, titleKey]);
  return <DashboardLayout title={title}>{children}</DashboardLayout>;
}

/**
 * The Calls screen wrapped in the dashboard shell with the tenant's AI Number in the page header ("Calls at <number>"), so the owner always sees which line the listed calls arrived on. Falls
 * back to the plain title while the number loads or before one is provisioned. Header-only: the nav item label stays "Calls".
 *
 * @returns The shell-wrapped Calls screen.
 */
function CallsScreen() {
  const { langVersion } = useLocale();
  // The AI Number is resolved from the authenticated owner server-side, so this read needs no tenantID argument.
  const number = useDashboardData((signal) => TenantClient.GetMyNumber(signal), []);
  const e164 = number.data?.e164 ?? null;
  const title = useMemo(() => {
    void langVersion;
    return e164 ? FormatLocalized(Localized.CallsTitleAt, e164) : Localized.CallsTitle;
  }, [langVersion, e164]);
  return (
    <DashboardLayout title={title}>
      <Calls />
    </DashboardLayout>
  );
}

/**
 * The Leads screen with the niche-specific noun in the header (e.g. "Patients" for a clinic). Header-only; the nav label is resolved the same way.
 *
 * @returns The shell-wrapped Leads screen.
 */
function LeadsScreen() {
  const labels = useNicheLabels();
  return (
    <DashboardLayout title={labels.leads}>
      <Leads />
    </DashboardLayout>
  );
}

/**
 * The Bookings screen with the niche-specific noun in the header (e.g. "Appointments" for a clinic).
 *
 * @returns The shell-wrapped Bookings screen.
 */
function BookingsScreen() {
  const labels = useNicheLabels();
  return (
    <DashboardLayout title={labels.bookings}>
      <Bookings />
    </DashboardLayout>
  );
}

/**
 * Authentication and onboarding gate for the dashboard route group. Renders the matched dashboard screen via {@link Outlet} once the authenticated account is fully onboarded, or an appropriate
 * gate state (login, loading, or the onboarding wizard) otherwise. Fail-closed: the dashboard never renders until onboarding is resolved AND complete.
 *
 * @returns The gated content element.
 */
function DashboardGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const { langLoaded } = useLocale();
  const { onboardingChecked, onboardingComplete, onboardingError, tenantID } = useDashboard();

  if (isLoading) return <FullScreenLoading />;
  if (!isAuthenticated) return <Login />;
  if (!langLoaded) return <FullScreenLoading />;
  // Fail-CLOSED onboarding gate: the dashboard nav and content never render until onboarding is fully resolved AND complete. Hold on loading until the first status read settles; while it is
  // unresolved, incomplete, or errored, render the wizard (which itself shows a try-again panel on a status error). This gates EVERY dashboard route — they are all Outlet children below.
  if (!onboardingChecked) return <FullScreenLoading />;
  if (!onboardingComplete || onboardingError) return <OnboardingWizard />;
  // Onboarding can resolve a render before the tenant user (which carries tenantID) settles — most visible on a hard refresh of a deep route, where pages would otherwise fire their reads with a
  // `null` tenantID (e.g. `…/subscription/null` → 403). Hold until tenantID is present so every page's tenant-scoped read has a real id from its first effect.
  if (!tenantID) return <FullScreenLoading />;

  return <Outlet />;
}

/**
 * Root translation gate. While a language switch is in flight the entire app (public Landing/About pages included, not just the dashboard) is replaced by a full-screen spinner, so the in-place
 * `Localized` dictionary is never rendered mid-mutation. When translation settles the whole route subtree remounts under the new language, giving a single clean reflow instead of a partial,
 * mixed-language repaint.
 *
 * @param props - Component props.
 * @param props.children - The routed application to gate.
 * @returns The children once the active language is loaded, otherwise the full-screen loader.
 */
function LocaleGate({ children }: { children: ReactNode }) {
  const { langLoaded } = useLocale();
  if (!langLoaded) return <FullScreenLoading />;
  return <>{children}</>;
}

/** @returns The rendered Vertex Echo tenant dashboard application. */
export function App() {
  return (
    <AuthAdapter>
      <LobbySettingsProvider>
        <DashboardProvider>
          <LayoutProvider>
            <ThemeProvider>
              <LocaleProvider>
                <LocaleGate>
                  <Router basename={localConstants.routerBasename}>
                    <Routes>
                      <Route path={Constants.Route.Landing} element={<Landing />} />
                      <Route path={Constants.Route.AboutDemo} element={<About />} />
                      <Route path={Constants.Route.AboutCompany} element={<About />} />
                      <Route path={Constants.Route.AboutTerms} element={<About />} />
                      <Route path={Constants.Route.AboutHelp} element={<About />} />
                      <Route path={Constants.Route.About} element={<Navigate to={Constants.Route.AboutDemo} replace />} />
                      <Route element={<DashboardGate />}>
                        <Route
                          path={Constants.Route.Home}
                          element={
                            <Screen titleKey="OverviewTitle">
                              <Overview />
                            </Screen>
                          }
                        />
                        <Route path={Constants.Route.Calls} element={<CallsScreen />} />
                        <Route
                          path={Constants.Route.CallDetail}
                          element={
                            <Screen titleKey="CallDetailTitle">
                              <CallDetail />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Texts}
                          element={
                            <Screen titleKey="TextsTitle">
                              <Texts />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.TextDetail}
                          element={
                            <Screen titleKey="TextDetailTitle">
                              <TextDetail />
                            </Screen>
                          }
                        />
                        <Route path={Constants.Route.Leads} element={<LeadsScreen />} />
                        <Route path={Constants.Route.Bookings} element={<BookingsScreen />} />
                        <Route
                          path={Constants.Route.Knowledge}
                          element={
                            <Screen titleKey="KnowledgeTitle">
                              <Knowledge />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.KnowledgeNew}
                          element={
                            <Screen titleKey="KnowledgeNewTitle">
                              <KnowledgeEdit />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.KnowledgeEdit}
                          element={
                            <Screen titleKey="KnowledgeEditTitle">
                              <KnowledgeEdit />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Profile}
                          element={
                            <Screen titleKey="ProfileTitle">
                              <BusinessProfile />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Integrations}
                          element={
                            <Screen titleKey="NavIntegrations">
                              <Integrations />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Notifications}
                          element={
                            <Screen titleKey="NavNotifications">
                              <Notifications />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Help}
                          element={
                            <Screen titleKey="AboutNavHelp">
                              <Help />
                            </Screen>
                          }
                        />
                        <Route
                          path={Constants.Route.Billing}
                          element={
                            <Screen titleKey="SharedBilling">
                              <Billing />
                            </Screen>
                          }
                        />
                        <Route path={Constants.Route.BillingReturn} element={<Navigate to={Constants.Route.Home} replace />} />
                        <Route
                          path={Constants.Route.Invoices}
                          element={
                            <Screen titleKey="InvoicesTitle">
                              <Invoices />
                            </Screen>
                          }
                        />
                        <Route
                          path={localConstants.dashboardWildcardRoute}
                          element={
                            <Screen titleKey="OverviewTitle">
                              <Overview />
                            </Screen>
                          }
                        />
                      </Route>
                      <Route path={localConstants.catchAllRoute} element={<Landing />} />
                    </Routes>
                  </Router>
                </LocaleGate>
              </LocaleProvider>
            </ThemeProvider>
          </LayoutProvider>
        </DashboardProvider>
      </LobbySettingsProvider>
    </AuthAdapter>
  );
}
